import { FullScreenOverlay } from '@components'

import { Button, Color } from '@billy/design-system'
import { ReactElement, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { useFetchContactById } from '@features/contacts/hooks/useFetchContactById'

import { KeyboardKey } from '../../../../enums/keyboardKey'
import { ReviewEntryStatus } from '../../enums/reviewEntryStatus'
import { useFetchReviewEntryAiReasoning } from '../../hooks/useFetchReviewEntryAiReasoning'
import { useMatchedSupplierContactId } from '../../hooks/useMatchedSupplierContactId'
import type { ReviewEntryLine } from '../../types/reviewEntryLine'
import type { ReviewEntryTableRow } from '../../types/reviewEntryTableRow'
import { hasThirdPartyQuestion } from '../../utils/reviewEntryThirdPartyQuestion'
import { ReviewEntryAiReasoning } from './elements/ReviewEntryAiReasoning'
import { ReviewEntryAiSuggestions } from './elements/ReviewEntryAiSuggestions'
import { REVIEW_ENTRY_DATA_FORM_ID, ReviewEntryDataForm } from './elements/ReviewEntryDataForm'
import { ReviewEntryDetailNavHeader } from './elements/ReviewEntryDetailNavHeader'
import { ReviewEntryDetailTitle } from './elements/ReviewEntryDetailTitle'
import { ReviewEntryDocumentViewer } from './elements/ReviewEntryDocumentViewer'
import { useSubmitReviewEntry } from './hooks/useSubmitReviewEntry'
import * as Styled from './styles'
import { getReviewEntryDetailTitle } from './utils/getReviewEntryDetailTitle'

// stable DOM contract of the DS `Dropdown` portal wrapper — its portal element carries this
// class and renders children only while the dropdown is open (mirrors the DS internal
// `DROPDOWN_CLASS_NAME`, which is not part of the DS public exports).
const DS_DROPDOWN_LIST_CLASS_NAME = 'ds-dropdown-list'

interface ReviewEntryDetailScreenProps {
  currentIndex?: number
  dismissedClarificationKeys: string[]
  documentId?: string
  entry: ReviewEntryTableRow
  entryLines: ReviewEntryLine[]
  entryReference?: string
  invoiceNumber?: string
  isBulkValidating?: boolean
  isForcedReadOnly?: boolean
  journalCode?: string
  onClose: () => void
  onDismissClarification: (questionKey: string) => void
  onNavigateNext?: () => void
  onNavigatePrevious?: () => void
  sourceFingerprint?: string
  supplierId?: string
  totalCount?: number
}

export const ReviewEntryDetailScreen = ({
  currentIndex,
  dismissedClarificationKeys,
  documentId,
  entry,
  entryLines,
  entryReference,
  invoiceNumber,
  isBulkValidating = false,
  isForcedReadOnly = false,
  journalCode,
  onClose,
  onDismissClarification,
  onNavigateNext,
  onNavigatePrevious,
  sourceFingerprint,
  supplierId,
  totalCount,
}: ReviewEntryDetailScreenProps): ReactElement => {
  const { t } = useTranslation()
  const normalizedStatus = entry.status?.toLowerCase()
  const isReadOnly =
    isForcedReadOnly ||
    normalizedStatus === ReviewEntryStatus.Validated ||
    normalizedStatus === ReviewEntryStatus.Written
  const isLastEntry = currentIndex != null && totalCount != null && currentIndex >= totalCount

  const isNewSupplier = hasThirdPartyQuestion(entry.questions)
  const { isLoading: isSupplierMatchLoading, supplierId: matchedSupplierId } = useMatchedSupplierContactId({
    supplierName: isNewSupplier || supplierId ? undefined : entry.supplierName,
  })

  const resolvedSupplierId = isNewSupplier ? undefined : (supplierId ?? matchedSupplierId ?? undefined)
  const {
    contact: supplierContact,
    isError: isSupplierContactError,
    isLoading: isSupplierContactLoading,
  } = useFetchContactById(resolvedSupplierId)
  const resolvedSupplierContact = isSupplierContactError ? undefined : supplierContact
  const isSupplierContactResolving = !!resolvedSupplierId && isSupplierContactLoading

  const { isLoading: isReasoningLoading, reasoning } = useFetchReviewEntryAiReasoning(documentId)
  const { isProcessing, onFormReadyChange, onSubmit } = useSubmitReviewEntry({
    documentId,
    entry,
    isLastEntry,
    journalCode,
    onClose,
    onNavigateNext,
    sourceFingerprint,
  })

  const isSubmitLocked = isProcessing || isBulkValidating

  const panelRef = useRef<HTMLDivElement>(null)

  // Escape closes the overlay unless a submit is in flight, an inline DS Dropdown is open
  // (e.g. the Supplier ContactSelect), or the escape came from a nested body-portal
  // (Modal, etc.). DS `Dropdown`/`Modal` don't call `preventDefault()` on their Escape
  // handler, so `event.defaultPrevented` cannot be relied on — instead we scope to escapes
  // that originate in this panel and detect open dropdowns via their DS portal wrapper.
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== KeyboardKey.Escape || isSubmitLocked) {
        return
      }

      const { target } = event
      const panel = panelRef.current
      const isTargetInsidePanel = target instanceof Node && panel !== null && panel.contains(target)
      const isTargetGlobal =
        target === null || target === document || target === document.body || target === document.documentElement

      if (!isTargetInsidePanel && !isTargetGlobal) {
        // escape originated from a sibling body-portal (e.g. an open DS Modal) — its own
        // handler will close it and this overlay must stay mounted
        return
      }

      const isDropdownOpen = Array.from(document.getElementsByClassName(DS_DROPDOWN_LIST_CLASS_NAME)).some(
        (element) => element.children.length > 0,
      )

      if (isDropdownOpen) {
        return
      }

      onClose()
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isSubmitLocked, onClose])

  return (
    <FullScreenOverlay bgColor={Color.Black} bgOpacity={0.2}>
      <Styled.ReviewEntryDetailPanel ref={panelRef}>
        <Styled.DocumentViewerWrapper>
          <ReviewEntryDocumentViewer billId={entry.billId} />
        </Styled.DocumentViewerWrapper>
        <Styled.EntriesContainer>
          <ReviewEntryDetailNavHeader
            currentIndex={currentIndex}
            onClose={onClose}
            onNavigateNext={onNavigateNext}
            onNavigatePrevious={onNavigatePrevious}
            totalCount={totalCount}
          />
          <Styled.ReviewEntryDetailContentArea>
            <ReviewEntryDetailTitle
              status={entry.status}
              title={getReviewEntryDetailTitle({
                invoiceNumber: entryReference,
                typeLabel: t('review_entries.detail.type.purchase'),
              })}
            />
            <ReviewEntryAiSuggestions
              dismissedQuestionKeys={dismissedClarificationKeys}
              entryId={entry.entryId}
              onDismissQuestion={onDismissClarification}
              questions={entry.questions}
            />
            <ReviewEntryAiReasoning isLoading={isReasoningLoading} reasoning={reasoning} />
            <Styled.EntryFormWrapper>
              <ReviewEntryDataForm
                currencyId={entry.currencyId}
                entryId={entry.entryId}
                entryLines={entryLines}
                invoiceNumber={invoiceNumber}
                isNewSupplier={isNewSupplier}
                isPrefillLoading={isSupplierMatchLoading || isSupplierContactResolving}
                isReadOnly={isReadOnly}
                isSubmitting={isSubmitLocked}
                key={entry.entryId}
                onFormReadyChange={onFormReadyChange}
                onSubmit={onSubmit}
                receiptDate={entry.date}
                sourceFingerprint={sourceFingerprint}
                supplierContact={resolvedSupplierContact}
                supplierId={resolvedSupplierContact?.id}
                supplierName={entry.supplierName}
              />
            </Styled.EntryFormWrapper>
          </Styled.ReviewEntryDetailContentArea>
          {!isReadOnly && (
            <Styled.ValidateRow>
              <Button disabled={isSubmitLocked} form={REVIEW_ENTRY_DATA_FORM_ID} loading={isSubmitLocked} type="submit">
                {t('review_entries.detail.validate')}
              </Button>
            </Styled.ValidateRow>
          )}
        </Styled.EntriesContainer>
      </Styled.ReviewEntryDetailPanel>
    </FullScreenOverlay>
  )
}
